import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../../UI/Buttons';
import { Overlay, Modal } from '../../UI/Containers';
import { Title } from '../../UI/Typography';

// Fix Leaflet icons (ensure this is consistent across your app)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// --- WKT Parsing Helper ---
const parseWktPolygon = (wktString) => {
  if (!wktString || !wktString.includes('POLYGON')) return null;
  try {
    const coordPairsStr = wktString.substring(wktString.indexOf('((') + 2, wktString.indexOf('))'));
    const pairs = coordPairsStr.split(',');
    return pairs.map(pair => {
      const [lng, lat] = pair.trim().split(' ').map(Number);
      if (isNaN(lat) || isNaN(lng)) throw new Error(`Invalid coordinate pair: ${pair}`);
      return [lat, lng]; // Leaflet uses [lat, lng]
    });
  } catch {
    return null;
  }
};
// --- End WKT Parsing Helper ---

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const MapContainer = styled.div`
  height: 450px;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lage};
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DEFAULT_CENTER = [6.9271, 79.8612];
const DEFAULT_ZOOM = 12;

const SiteBoundaryMapModal = ({ isOpen, onClose, siteName, boundaryWkt }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (isOpen && mapRef.current) {
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = L.map(mapRef.current).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap contributors'
        }).addTo(mapInstanceRef.current);
      }
      const map = mapInstanceRef.current;
      // Remove existing polygons
      map.eachLayer(layer => {
        if (layer instanceof L.Polygon) map.removeLayer(layer);
      });
      // Draw new polygon if available
      if (boundaryWkt) {
        const coordinates = parseWktPolygon(boundaryWkt);
        if (coordinates && coordinates.length > 0) {
          const polygon = L.polygon(coordinates, { color: 'blue' }).addTo(map);
          map.fitBounds(polygon.getBounds().pad(0.1));
        } else {
          map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
        }
      } else {
        map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      }
      // Fix map rendering issues on modal open
      const timerId = setTimeout(() => map.invalidateSize(), 150);
      return () => clearTimeout(timerId);
    } else if (!isOpen && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  }, [isOpen, boundaryWkt]);

  if (!isOpen) return null;

  return (
    <Overlay opacity={0.7} onClick={onClose}>
      <Modal size="large" maxWidth="700px" onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title as="h2" style={{ margin: 0, fontSize: '1.2em' }}>
            Boundary for: {siteName || 'Site'}
          </Title>
        </ModalHeader>
        <MapContainer ref={mapRef} id="boundary-map-container" />
        <ButtonGroup>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default SiteBoundaryMapModal;
